import React, { useEffect, useRef, useState } from 'react'
import { l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { Chip } from 'apps/cms/components/Flows/components/FlowCard/components/Triggers/components/Chip/Chip'
import { useFlexibleTriggerNameClassName } from 'apps/cms/components/Flows/components/FlowCard/components/Triggers/hooks/useFlexibleTriggerNameClassName'
import { renderTextByNodes } from 'apps/cms/components/Flows/components/FlowCard/components/Triggers/lib/renderTextByNodes'
import { contentManagementSelectors } from 'apps/cms/store'
import { getKeywordRuleConditionLabel } from 'apps/growthTools/constants/keywordRuleConditions'
import { PostCoveredArea } from 'apps/growthTools/models/Widget/constants'
import { TikTokCommentSchema } from 'shared/api/requests/cms/schemas/widgets'

import { TikTokCommentTitleImg } from './TikTokCommentTitleImg'

interface TikTokCommentTitleProps {
  widget: TikTokCommentSchema
}

export const TikTokCommentTitle = ({ widget }: TikTokCommentTitleProps) => {
  const hasSearchValue = useAppSelector(contentManagementSelectors.getHasSearchValue)

  const commentSettings = widget.data?.feed_comment_settings || {}

  const titleRef = useRef<HTMLSpanElement | null>(null)

  const [postImageUrl, setPostImageUrl] = useState('')

  useEffect(() => {
    if (!commentSettings.post_data || !titleRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0].isIntersecting) {
          setPostImageUrl(commentSettings.post_data?.thumbnail_url ?? '')
          observer.disconnect()
        }
      },
      {
        threshold: 1.0,
      },
    )

    observer.observe(titleRef.current)

    return () => observer.disconnect()
  }, [commentSettings.post_data])

  const renderHighIntentKeywords = () => {
    const keywordRules = commentSettings.keyword_rules || []

    if (!keywordRules?.length) {
      return null
    }

    return keywordRules.map((rule, ruleIndex) => {
      const hasKeywords = rule.keywords && rule.keywords.length > 0

      if (!hasKeywords) {
        return null
      }

      return (
        <React.Fragment key={ruleIndex}>
          {ruleIndex > 0 && renderTextByNodes(l.translate('or'))}{' '}
          {renderTextByNodes(getKeywordRuleConditionLabel(rule.condition))}{' '}
          {rule.keywords.map((keyword, index) => (
            <Chip label={keyword} key={index} />
          ))}
        </React.Fragment>
      )
    })
  }

  const contentClassName = useFlexibleTriggerNameClassName()

  const widgetName = hasSearchValue ? renderTextByNodes(`${widget.name} •`) : ''

  if (
    'post_covered_area' in commentSettings &&
    commentSettings.post_covered_area !== PostCoveredArea.SPECIFIC_POST
  ) {
    return (
      <span className={contentClassName}>
        {widgetName}

        {commentSettings.post_covered_area === PostCoveredArea.NEXT_POST &&
          renderTextByNodes(l.translate('User comments on next TikTok'))}

        {commentSettings.post_covered_area === PostCoveredArea.ALL_POSTS &&
          renderTextByNodes(l.translate('User comments on any TikTok'))}

        {renderHighIntentKeywords()}
      </span>
    )
  }

  return (
    <span className={contentClassName} ref={titleRef}>
      {widgetName}

      {renderTextByNodes(
        commentSettings.keyword_rules?.length
          ? l.translate('User’s comment on specific TikTok')
          : l.translate('User comments on specific TikTok'),
      )}
      {commentSettings.post_data && <TikTokCommentTitleImg url={postImageUrl} />}
      {renderHighIntentKeywords()}
    </span>
  )
}
